import React, { useEffect, useRef } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/16/solid';
import { classNames } from '../../utils/classNames';

// A small inline editor for a percent value. The value is a string, as the input holds it.
// Enter calls onSubmit, Esc calls onCancel, a blur calls onCancel too. The caller owns the value.
type Props = {
	value: string,
	onChange: (value: string) => void,
	onSubmit: () => void,
	onCancel: () => void,
	disabled?: boolean,
	showActions?: boolean,
	submitDisabled?: boolean,
	autoFocus?: boolean,
	ariaLabel?: string,
	hint?: string,
	className?: string,
};

const PercentEditor: React.FunctionComponent<Props> = ({
	value,
	onChange,
	onSubmit,
	onCancel,
	disabled = false,
	showActions = false,
	submitDisabled = false,
	autoFocus = true,
	ariaLabel = 'Percent',
	hint = 'Enter · Esc',
	className,
}) => {
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (autoFocus) inputRef.current?.focus();
	}, [autoFocus]);

	return <div className={classNames('flex flex-col gap-y-0.5', className ?? '')}
    onBlur={event => { if (showActions && !disabled && !event.currentTarget.contains(event.relatedTarget)) onCancel(); }}
    onKeyDownCapture={event => { if (showActions && !disabled && event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onCancel(); } }}>
    <div className={showActions ? 'flex items-center gap-1' : 'contents'}>
		<span className="relative inline-flex w-14">
			<input ref={inputRef}
							type="number"
							step={1}
							value={value}
							aria-label={ariaLabel}
							title="Enter submits, Esc cancels"
							disabled={disabled}
							onChange={event => onChange(event.target.value)}
							onKeyDown={event => {
								if (event.key === 'Enter') { event.preventDefault(); if (!disabled && !submitDisabled) onSubmit(); }
								if (event.key === 'Escape') { event.preventDefault(); onCancel(); }
							}}
							onBlur={() => { if (!disabled && !showActions) onCancel(); }}
							className={classNames("w-14 rounded-md border-0 pl-1.5 pr-4 text-xs text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-blue-600", showActions ? "h-7 py-1" : "py-0.5")} />
			<span className="pointer-events-none absolute inset-y-0 right-1 flex items-center text-xs text-gray-400">%</span>
		</span>
      {showActions && <>
        <button type="button" aria-label="Cancel" title="Cancel (Esc)" disabled={disabled} onMouseDown={event => event.preventDefault()} onClick={onCancel}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"><XMarkIcon className="h-4 w-4" aria-hidden="true" /></button>
        <button type="button" aria-label="Confirm percent change" title="Confirm (Enter)" disabled={disabled || submitDisabled} onMouseDown={event => event.preventDefault()} onClick={onSubmit}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-blue-600 text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"><CheckIcon className="h-4 w-4" aria-hidden="true" /></button>
      </>}
    </div>
		{hint !== '' && <span className="text-xs text-gray-400">{hint}</span>}
	</div>;
};

export default PercentEditor;
